import { create } from "zustand"
import type { CompositorConfig, CompositorLayer, CompositorTransform, CompositorTextStyle, CompositorCanvas } from "@/lib/types/compositor"
import { DEFAULT_TEXT_STYLE } from "@/lib/types/compositor"
import { usePipelineStore } from "@/lib/stores/pipeline-store"

const MAX_UNDO = 50

interface CompositorStore {
  // Editor context
  isOpen: boolean
  nodeId: string | null
  config: CompositorConfig | null

  // Selection
  selectedItem: { type: "canvas" | "layer"; layerId?: string } | null

  // Undo/redo
  undoStack: CompositorConfig[]
  redoStack: CompositorConfig[]

  // Playback
  isPlaying: boolean
  currentTime: number
  volume: number
  isMuted: boolean

  // Viewport
  zoom: number
  panX: number
  panY: number

  // Export
  requestExport: number  // bumped to trigger canvas export

  // Close with snapshot
  requestSnapshot: number  // bumped to trigger preview snapshot before close

  // Actions
  openEditor: (nodeId: string) => void
  closeEditor: () => void
  selectItem: (item: { type: "canvas" | "layer"; layerId?: string } | null) => void
  updateLayer: (layerId: string, patch: Partial<CompositorLayer>) => void
  updateTransform: (layerId: string, patch: Partial<CompositorTransform>) => void
  updateTextStyle: (layerId: string, patch: Partial<CompositorTextStyle>) => void
  updateCanvas: (patch: Partial<CompositorCanvas>) => void
  addLayer: (layer: CompositorLayer) => void
  removeLayer: (layerId: string) => void
  reorderLayers: (layers: CompositorLayer[]) => void
  undo: () => void
  redo: () => void
  setPlaying: (playing: boolean) => void
  seek: (time: number) => void
  setVolume: (volume: number) => void
  setMuted: (muted: boolean) => void
  setZoom: (zoom: number) => void
  setPan: (x: number, y: number) => void
  triggerExport: () => void
  prepareClose: () => void  // triggers snapshot, canvas calls finalizeClose when done
  finalizeClose: (dataUrl: string) => void  // stores renderedOutput and closes
}

function snapshot(config: CompositorConfig): CompositorConfig {
  return JSON.parse(JSON.stringify(config))
}

function syncToPipeline(nodeId: string, config: CompositorConfig) {
  usePipelineStore.getState().updateNodeConfig(
    nodeId,
    config as unknown as Record<string, unknown>
  )
}

export const useCompositorStore = create<CompositorStore>((set, get) => ({
  isOpen: false,
  nodeId: null,
  config: null,
  selectedItem: null,
  undoStack: [],
  redoStack: [],
  isPlaying: false,
  currentTime: 0,
  volume: 100,
  isMuted: false,
  zoom: 1,
  panX: 0,
  panY: 0,
  requestExport: 0,
  requestSnapshot: 0,

  openEditor: (nodeId) => {
    const node = usePipelineStore.getState().nodes.find((n) => n.id === nodeId)
    if (!node) return
    const config = snapshot(node.data.config as unknown as CompositorConfig)
    set({
      isOpen: true,
      nodeId,
      config,
      selectedItem: { type: "canvas" },
      undoStack: [],
      redoStack: [],
      currentTime: 0,
      isPlaying: false,
    })
  },

  closeEditor: () => {
    set({ isOpen: false, nodeId: null, config: null, selectedItem: null })
  },

  selectItem: (item) => set({ selectedItem: item }),

  updateLayer: (layerId, patch) => {
    const { config, nodeId } = get()
    if (!config || !nodeId) return
    const prev = snapshot(config)
    const layers = config.layers.map((l) => l.id === layerId ? { ...l, ...patch } : l)
    const next = { ...config, layers }
    set((s) => ({
      config: next,
      undoStack: [...s.undoStack.slice(-(MAX_UNDO - 1)), prev],
      redoStack: [],
    }))
    syncToPipeline(nodeId, next)
  },

  updateTransform: (layerId, patch) => {
    const { config, nodeId } = get()
    if (!config || !nodeId) return
    const prev = snapshot(config)
    const layers = config.layers.map((l) =>
      l.id === layerId ? { ...l, transform: { ...l.transform, ...patch } } : l
    )
    const next = { ...config, layers }
    set((s) => ({
      config: next,
      undoStack: [...s.undoStack.slice(-(MAX_UNDO - 1)), prev],
      redoStack: [],
    }))
    syncToPipeline(nodeId, next)
  },

  updateTextStyle: (layerId, patch) => {
    const { config, nodeId } = get()
    if (!config || !nodeId) return
    const prev = snapshot(config)
    const layers = config.layers.map((l) => {
      if (l.id !== layerId) return l
      return { ...l, textStyle: { ...DEFAULT_TEXT_STYLE, ...l.textStyle, ...patch } as CompositorTextStyle }
    })
    const next = { ...config, layers }
    set((s) => ({
      config: next,
      undoStack: [...s.undoStack.slice(-(MAX_UNDO - 1)), prev],
      redoStack: [],
    }))
    syncToPipeline(nodeId, next)
  },

  updateCanvas: (patch) => {
    const { config, nodeId } = get()
    if (!config || !nodeId) return
    const prev = snapshot(config)
    const next = { ...config, canvas: { ...config.canvas, ...patch } }
    set((s) => ({
      config: next,
      undoStack: [...s.undoStack.slice(-(MAX_UNDO - 1)), prev],
      redoStack: [],
    }))
    syncToPipeline(nodeId, next)
  },

  addLayer: (layer) => {
    const { config, nodeId } = get()
    if (!config || !nodeId) return
    const prev = snapshot(config)
    const next = { ...config, layers: [...config.layers, layer] }
    set((s) => ({
      config: next,
      undoStack: [...s.undoStack.slice(-(MAX_UNDO - 1)), prev],
      redoStack: [],
    }))
    syncToPipeline(nodeId, next)
  },

  removeLayer: (layerId) => {
    const { config, nodeId } = get()
    if (!config || !nodeId) return
    const prev = snapshot(config)
    const next = { ...config, layers: config.layers.filter((l) => l.id !== layerId) }
    set((s) => ({
      config: next,
      undoStack: [...s.undoStack.slice(-(MAX_UNDO - 1)), prev],
      redoStack: [],
    }))
    syncToPipeline(nodeId, next)
  },

  reorderLayers: (layers) => {
    const { config, nodeId } = get()
    if (!config || !nodeId) return
    const prev = snapshot(config)
    const next = { ...config, layers }
    set((s) => ({
      config: next,
      undoStack: [...s.undoStack.slice(-(MAX_UNDO - 1)), prev],
      redoStack: [],
    }))
    syncToPipeline(nodeId, next)
  },

  undo: () => {
    const { undoStack, config, nodeId } = get()
    if (undoStack.length === 0 || !config || !nodeId) return
    const prev = undoStack[undoStack.length - 1]
    set((s) => ({
      config: prev,
      undoStack: s.undoStack.slice(0, -1),
      redoStack: [snapshot(config), ...s.redoStack],
    }))
    syncToPipeline(nodeId, prev)
  },

  redo: () => {
    const { redoStack, config, nodeId } = get()
    if (redoStack.length === 0 || !config || !nodeId) return
    const next = redoStack[0]
    set((s) => ({
      config: next,
      redoStack: s.redoStack.slice(1),
      undoStack: [...s.undoStack, snapshot(config)],
    }))
    syncToPipeline(nodeId, next)
  },

  setPlaying: (isPlaying) => set({ isPlaying }),
  seek: (currentTime) => set({ currentTime }),
  setVolume: (volume) => set({ volume }),
  setMuted: (isMuted) => set({ isMuted }),
  setZoom: (zoom) => set({ zoom }),
  setPan: (panX, panY) => set({ panX, panY }),
  triggerExport: () => set((s) => ({ requestExport: s.requestExport + 1 })),

  prepareClose: () => set((s) => ({ requestSnapshot: s.requestSnapshot + 1 })),

  finalizeClose: (dataUrl) => {
    const { config, nodeId } = get()
    if (config && nodeId) {
      const next = { ...config, renderedOutput: dataUrl }
      syncToPipeline(nodeId, next)
    }
    set({ isOpen: false, nodeId: null, config: null, selectedItem: null })
  },
}))
